import React, { useState, useEffect, useRef } from 'react';
import {
  MaterialReactTable,
  MRT_DensityState,
  MRT_TableInstance
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';

interface PersistentTableProps<TData = any> {
  tableId: string;
  columns: any[];
  data: TData[];
  rowClick?: Function;
  height?: string;
  totalRows?: number; // Adding totalRows for pagination
  fetchData?: Function;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  enableBottomToolbar?: boolean;
  enableRowSelection?: boolean;
  renderTopToolbarCustomActions?: ({
    table
  }: {
    table: MRT_TableInstance<any>;
  }) => React.ReactNode;
  enablePagination?: boolean;
  manualPagination?: boolean;
  defaultPin?: string | null;
  defaultPageSize?: number;
  initialColumnVisibility?: any;
  dynamicColumns?: any;
  setParentSorting?: Function;
  manualSorting?: boolean;
  isLoading?: boolean;
  enableRowVirtualization?: boolean;
  enableColumnFilters?: boolean;
  setGlobalFilter?: Function;
  manualFiltering?: boolean;
}

interface StateObject {
  columnFilters: any[];
  columnVisibility: any;
  density: MRT_DensityState;
  globalFilter: string | undefined;
  showGlobalFilter: boolean;
  showColumnFilters: boolean;
  sorting: any[];
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  isLoading: boolean;
}

const PersistentTable = ({
  tableId,
  columns,
  data,
  rowClick,
  height = '1000px',
  totalRows = undefined,
  fetchData,
  onRowSelectionChange,
  enableBottomToolbar = false,
  enableRowSelection = false,
  renderTopToolbarCustomActions,
  enablePagination = false,
  manualPagination = true,
  defaultPin = 'name',
  defaultPageSize = 25,
  initialColumnVisibility = {},
  dynamicColumns = {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setParentSorting = () => {},
  manualSorting = false,
  isLoading = false,
  enableRowVirtualization = false,
  enableColumnFilters = true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setGlobalFilter = () => {},
  manualFiltering = false
}: PersistentTableProps) => {
  const isFirstRender = useRef(true);
  const uniqueTableName = `${tableId}_state`;

  const getLocalStorageData = (key: string, returnType: any = []) => {
    const storedValue = localStorage.getItem(uniqueTableName);
    return storedValue && JSON.parse(storedValue)[key] !== undefined
      ? JSON.parse(storedValue)[key]
      : returnType;
  };

  const setupColumnVisibility = (overrides = {}) => {
    return {
      ...initialColumnVisibility,
      ...getLocalStorageData('columnVisibility', {}),
      ...dynamicColumns,
      ...overrides
    };
  };

  const [columnFilters, setColumnFilters] = useState<any[]>(() =>
    getLocalStorageData('columnFilters', [])
  );
  const [sorting, setSorting] = useState<any[]>(() =>
    getLocalStorageData('sorting', [])
  );
  const [columnVisibility, setColumnVisibility] = useState(() => {
    return setupColumnVisibility();
  });
  const [pagination, setPagination] = useState(() =>
    getLocalStorageData('pagination', {
      pageIndex: 0,
      pageSize: defaultPageSize
    })
  );

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const [state, setState] = useState<StateObject>({
    columnFilters: columnFilters,
    columnVisibility: columnVisibility,
    density: 'comfortable',
    globalFilter: undefined,
    showGlobalFilter: false,
    showColumnFilters: false,
    sorting: sorting,
    pagination,
    isLoading: false
  });

  useEffect(() => {
    const storageKey = uniqueTableName;
    const storedValue = localStorage.getItem(storageKey);
    if (storedValue) {
      setState(JSON.parse(storedValue));
    }
    isFirstRender.current = false;
  }, [tableId]);
  useEffect(() => {
    if (!isFirstRender.current) {
      const storageKey = uniqueTableName;
      localStorage.setItem(
        storageKey,
        JSON.stringify({ ...state, pagination })
      );
    }
  }, [state, pagination, tableId]);
  useEffect(() => {
    setState((prev: any) => ({
      ...prev,
      columnFilters,
      sorting,
      columnVisibility: setupColumnVisibility(columnVisibility),
      isLoading,
      pagination: { pageIndex: 0, pageSize: pagination.pageSize }
    }));
  }, [columnFilters, sorting, columnVisibility, isLoading, pagination]);

  useEffect(() => {
    if (fetchData) {
      fetchData({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize
      });
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = Object.keys(rowSelection)
        .filter(key => rowSelection[key])
        .map(key => data[parseInt(key, 10)]);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      localization={MRT_Localization_EN}
      enableColumnResizing={true}
      enableColumnPinning={true}
      enablePagination={enablePagination}
      manualPagination={manualPagination}
      manualSorting={manualSorting}
      enableColumnFilters={enableColumnFilters}
      manualFiltering={manualFiltering}
      rowCount={totalRows}
      enableBottomToolbar={enableBottomToolbar}
      layoutMode={'grid'}
      enableFullScreenToggle={false}
      enableColumnFilterModes={true}
      enableRowVirtualization={enableRowVirtualization}
      initialState={{
        density: 'compact',
        columnVisibility: initialColumnVisibility,
        ...(enableRowSelection
          ? {}
          : { columnPinning: { left: defaultPin ? [defaultPin] : [] } })
      }}
      enableRowSelection={enableRowSelection}
      enableFacetedValues={true}
      enableStickyHeader={true}
      muiTableContainerProps={{
        sx: { maxHeight: height }
      }}
      muiTableHeadCellProps={{
        sx: {
          fontFamily: 'Gilroy'
        }
      }}
      muiFilterDatePickerProps={{ format: 'DD/MM/YYYY' }}
      muiTableBodyCellProps={({ cell }) => ({
        onClick: () => {
          if (rowClick) {
            rowClick(cell);
          }
        },
        sx: {
          fontFamily: 'Gilroy'
        }
      })}
      onColumnFiltersChange={filters => {
        setColumnFilters(filters);
      }}
      onColumnVisibilityChange={visibility => {
        setColumnVisibility(visibility);
      }}
      onDensityChange={density =>
        setState((prev: any) => ({ ...prev, density }))
      }
      onGlobalFilterChange={filter => {
        setGlobalFilter(filter);
        setState((prev: any) => ({ ...prev, globalFilter: filter }));
      }}
      onShowColumnFiltersChange={show =>
        setState((prev: any) => ({ ...prev, showColumnFilters: show }))
      }
      onShowGlobalFilterChange={show =>
        setState((prev: any) => ({ ...prev, showGlobalFilter: show }))
      }
      onSortingChange={sorting => {
        setParentSorting(sorting);
        setSorting(sorting);
      }}
      onPaginationChange={pagination => {
        setPagination(pagination);
      }}
      onRowSelectionChange={rowSelection => {
        setRowSelection(rowSelection);
      }}
      state={{
        ...state,
        pagination: { ...pagination },
        rowSelection: { ...rowSelection }
      }}
      renderTopToolbarCustomActions={renderTopToolbarCustomActions}
    />
  );
};

export default PersistentTable;
